import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'

const TITLE_ID = 'filter-sheet-title'

interface Props {
  open: boolean
  /** Every brand the storefront sells, alphabetically. Empty while loading. */
  brands: readonly string[]
  loading: boolean
  /** The brands already applied, so reopening the sheet shows the current filter. */
  selected: readonly string[]
  onClose: () => void
  onApply: (brands: readonly string[]) => void
}

/**
 * The brand filter.
 *
 * A sheet rather than a second chip rail: the category rail is one choice among
 * a handful and belongs on screen, while brands are a long list that only
 * matters once — picking two or three and leaving them applied. Putting them
 * inline would push the grid below the fold on every storefront with more than
 * a few brands.
 *
 * Multi-select, and a draft until "Aplicar": each toggle would otherwise be a
 * round trip, so a shopper picking three brands would watch the grid rebuild
 * three times. `VisitSheet` takes the same draft-then-confirm shape, and the
 * overlay behaviour — focus trap, Escape, backdrop — comes from `Drawer`.
 */
export const FilterSheet = ({
  open,
  brands,
  loading,
  selected,
  onClose,
  onApply,
}: Props) => {
  const [pending, setPending] = useState<readonly string[]>(selected)

  // Re-seed on each opening rather than on every render: while the sheet is
  // open the draft belongs to the shopper, not to the page.
  useEffect(() => {
    if (open) setPending(selected)
  }, [open, selected])

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': TITLE_ID,
      }}
    >
      <Box sx={{ p: 2.5, pb: 'calc(20px + env(safe-area-inset-bottom))' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Typography id={TITLE_ID} variant="h3" component="h2">
            Marcas
          </Typography>
          <IconButton onClick={onClose} aria-label="Cerrar" sx={{ mt: -0.5, mr: -1 }}>
            ×
          </IconButton>
        </Stack>

        {loading && brands.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} aria-label="Cargando marcas" />
          </Box>
        )}

        {!loading && brands.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Este catálogo no tiene marcas para filtrar.
          </Typography>
        )}

        {brands.length > 0 && (
          <ToggleButtonGroup
            value={[...pending]}
            onChange={(_event, next: string[]) => setPending(next)}
            aria-label="Marcas"
            sx={{
              mt: 2,
              flexWrap: 'wrap',
              gap: 0.75,
              '& .MuiToggleButtonGroup-grouped': {
                border: `1px solid ${YALO_SEMANTIC.borderDefault}`,
                borderRadius: `${YALO_RADIUS.pill}px`,
                height: 34,
                px: 1.75,
                fontSize: 13,
                fontWeight: 600,
                color: YALO_SEMANTIC.fg2,
                textTransform: 'none',
              },
              '& .MuiToggleButtonGroup-grouped.Mui-selected': {
                bgcolor: 'primary.main',
                borderColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}
          >
            {brands.map((brand) => (
              <ToggleButton key={brand} value={brand}>
                {brand}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
          <Button
            fullWidth
            size="large"
            variant="outlined"
            // Clearing is an action of its own rather than "deselect them all":
            // with ten brands applied, unpicking each one is ten taps.
            disabled={pending.length === 0}
            onClick={() => setPending([])}
          >
            Limpiar
          </Button>
          <Button fullWidth size="large" variant="contained" onClick={() => onApply(pending)}>
            Aplicar
          </Button>
        </Stack>
      </Box>
    </Drawer>
  )
}
